// src/pages/DisputedRecords.jsx
//
// "Disputed Records" — third tab on /admin/cron-health (global_admin only).
//
// The human override for `tenant_disputed_records`. A lead denying an injected fact
// ("that's not my case") is recorded against the VALUE, not the lead, so it is visible
// to every thread carrying that same value. On Exodus, 645 of 2,993 rows with a Case
// Number share it with another lead and one case sits on 6 leads. That is exactly why a
// human has to be able to overrule the automation: an automated denial is not proof, and
// a single wrong dispute would otherwise degrade outreach for up to five other people.
//
// WHY A TAB, NOT A CARD ON OPS MONITOR:
// Ops Monitor is a 2x2 glance grid on a 30s auto-refresh. This is a decision workflow —
// read the evidence, see who it hits, type a reason, commit. Auto-refresh under a
// half-typed note is hostile, and the blast-radius list needs real vertical space.
// So: no auto-refresh here. Refresh is manual and explicit.
//
// TIMEZONE RULE: every timestamp is rendered in America/New_York via fmtET(). No UTC.
//
// Data: GET /api/admin/disputed-records, POST /api/admin/disputed-records/:id/resolve
// (both global_admin gated, via fetchWithCsrf).

import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldAlert, ShieldCheck, ShieldX, RefreshCw, ExternalLink,
  ChevronDown, ChevronRight, Users, Quote, AlertTriangle, Info,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchWithCsrf } from '../lib/fetchWithCsrf';

// ─── Time helper — ALL display goes through America/New_York ──────────────────

function fmtET(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    hour12: true, timeZoneName: 'short',
  }).format(d);
}

// ─── Status vocabulary ────────────────────────────────────────────────────────

const STATUS_META = {
  disputed: {
    label: 'Needs review',
    icon: ShieldAlert,
    chip: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  verified_true: {
    label: 'Verified true',
    icon: ShieldCheck,
    chip: 'bg-green-100 text-green-800 border-green-200',
  },
  confirmed_wrong: {
    label: 'Confirmed wrong',
    icon: ShieldX,
    chip: 'bg-red-100 text-red-800 border-red-200',
  },
};

const FILTERS = [
  { key: 'disputed', label: 'Needs review' },
  { key: 'verified_true', label: 'Verified true' },
  { key: 'confirmed_wrong', label: 'Confirmed wrong' },
  { key: 'all', label: 'All' },
];

/**
 * What the record does to outreach, in plain language.
 * Mirrors resolveForLead() in supabase/functions/_shared/disputedRecords.ts. If that
 * resolution logic changes, this copy has to change with it or the page starts lying.
 */
function effectCopy(status, affected, disputedBy) {
  const others = Math.max(0, (affected ?? 0) - disputedBy);
  if (status === 'verified_true') {
    return {
      tone: 'text-green-800 bg-green-50 border-green-200',
      text: `Used normally on all ${affected ?? '?'} leads. Further denials from leads will not reopen this.`,
    };
  }
  if (status === 'confirmed_wrong') {
    return {
      tone: 'text-red-800 bg-red-50 border-red-200',
      text: `Excluded from every lead that carries it (${affected ?? '?'}). It will not be stated again.`,
    };
  }
  return {
    tone: 'text-amber-900 bg-amber-50 border-amber-200',
    text: `Excluded on the ${disputedBy} lead${disputedBy === 1 ? '' : 's'} who denied it. `
      + `Injected with an "unverified" caveat on the other ${others} lead${others === 1 ? '' : 's'} `
      + `that ${others === 1 ? 'shares' : 'share'} this value, because one contact's denial is not `
      + `proof the record is wrong for everyone.`,
  };
}

function StatusChip({ status }) {
  const meta = STATUS_META[status] || STATUS_META.disputed;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${meta.chip}`}>
      <Icon className="w-3.5 h-3.5" />
      {meta.label}
    </span>
  );
}

// ─── Resolve panel — two-step, because this is consequential ───────────────────

function ResolvePanel({ record, onResolved }) {
  const [armed, setArmed] = useState(null);   // 'verified_true' | 'confirmed_wrong'
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const affected = record.affected_leads;
  const isFlip = record.status !== 'disputed';

  async function commit() {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetchWithCsrf(`/api/admin/disputed-records/${record.id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: armed, note: note.trim() || undefined }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setArmed(null);
      setNote('');
      onResolved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!armed) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setArmed('verified_true')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-300 bg-white text-green-800 text-sm font-medium hover:bg-green-50"
        >
          <ShieldCheck className="w-4 h-4" />
          Record stands
        </button>
        <button
          onClick={() => setArmed('confirmed_wrong')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-300 bg-white text-red-800 text-sm font-medium hover:bg-red-50"
        >
          <ShieldX className="w-4 h-4" />
          Record is wrong
        </button>
        <span className="text-xs text-gray-500">
          {isFlip
            ? 'Already resolved. Choosing again overwrites the verdict and keeps the old one in the note.'
            : 'Check the source (docket, county site) before deciding. A denial is not proof either way.'}
        </span>
      </div>
    );
  }

  const preview = effectCopy(armed, affected, record.disputed_by_count || 0);

  return (
    <div className="rounded-lg border border-gray-300 bg-gray-50 p-3 space-y-2.5">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-gray-500 mt-0.5 flex-none" />
        <div className="text-sm text-gray-800">
          <span className="font-semibold">
            {armed === 'verified_true' ? 'Mark verified true' : 'Mark confirmed wrong'}
          </span>{' '}
          for <span className="font-mono">{record.field_key}</span> ={' '}
          <span className="font-mono">{record.value_raw}</span>.
          <div className={`mt-1.5 rounded border px-2 py-1.5 text-xs ${preview.tone}`}>
            {preview.text}
          </div>
        </div>
      </div>

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="How did you check? (e.g. pulled the docket, case is closed as of 6/12)"
        className="w-full text-sm rounded-lg border border-gray-300 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />

      {err && <div className="text-xs text-red-700">{err}</div>}

      <div className="flex items-center gap-2">
        <button
          onClick={commit}
          disabled={busy}
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 ${
            armed === 'verified_true' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
          }`}
        >
          {busy ? 'Saving...' : 'Confirm'}
        </button>
        <button
          onClick={() => { setArmed(null); setErr(null); }}
          disabled={busy}
          className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── One dispute ──────────────────────────────────────────────────────────────

function DisputeRow({ record, onResolved }) {
  const [open, setOpen] = useState(record.status === 'disputed');
  const effect = effectCopy(record.status, record.affected_leads, record.disputed_by_count || 0);
  const evidence = Array.isArray(record.evidence) ? record.evidence : [];
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
      >
        <Chevron className="w-4 h-4 text-gray-400 flex-none" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">{record.field_key}</span>
            <span className="font-mono text-sm text-gray-700 bg-gray-100 rounded px-1.5 py-0.5">
              {record.value_raw}
            </span>
            <StatusChip status={record.status} />
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {record.tenant_name} · denied by {record.disputed_by_count} lead
            {record.disputed_by_count === 1 ? '' : 's'} · last {fmtET(record.last_disputed_at)}
          </div>
        </div>
        <div className="flex-none inline-flex items-center gap-1.5 text-sm text-gray-600">
          <Users className="w-4 h-4 text-gray-400" />
          <span className="font-semibold text-gray-900">
            {record.affected_leads == null ? '?' : record.affected_leads}
          </span>
          <span className="text-xs text-gray-500">
            lead{record.affected_leads === 1 ? '' : 's'} affected
          </span>
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-3">
          <div className={`rounded-lg border px-3 py-2 text-sm ${effect.tone}`}>
            <span className="font-semibold">Right now: </span>{effect.text}
          </div>

          {/* Blast radius */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              Leads carrying this value
            </div>
            {record.affected_lead_sample?.length ? (
              <div className="flex flex-wrap gap-1.5">
                {record.affected_lead_sample.map((l) => (
                  <Link
                    key={l.id}
                    to={`/lead/${l.id}`}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                  >
                    {l.name || 'Unknown'}
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                ))}
                {record.affected_leads > record.affected_lead_sample.length && (
                  <span className="inline-flex items-center text-xs px-2 py-1 text-gray-500">
                    +{record.affected_leads - record.affected_lead_sample.length} more
                  </span>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-500">
                No lead currently carries this exact value.
              </div>
            )}
          </div>

          {/* Evidence */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              What the lead actually said
            </div>
            {evidence.length ? (
              <ul className="space-y-1.5">
                {evidence.map((e, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Quote className="w-3.5 h-3.5 text-gray-400 mt-1 flex-none" />
                    <div>
                      <span className="text-gray-900">"{e.quote || '(no quote captured)'}"</span>
                      <span className="text-xs text-gray-500 ml-2">
                        {e.lead_id ? (
                          <Link to={`/lead/${e.lead_id}`} className="text-indigo-600 hover:text-indigo-800">
                            view thread
                          </Link>
                        ) : null}
                        {e.at ? ` · ${fmtET(e.at)}` : ''}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-gray-500">No quote recorded.</div>
            )}
          </div>

          {/* Prior human verdict */}
          {record.resolved_at && (
            <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              Resolved by <span className="font-medium">{record.resolved_by || 'unknown'}</span> on{' '}
              {fmtET(record.resolved_at)}
              {record.resolution_note ? <> · {record.resolution_note}</> : null}
            </div>
          )}

          <ResolvePanel record={record} onResolved={onResolved} />
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DisputedRecords() {
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('disputed');

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetchWithCsrf('/api/admin/disputed-records');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setRecords(json.records || []);
    } catch (e) {
      setErr(e.message);
      setRecords(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = (records || []).filter((r) => filter === 'all' || r.status === filter);
  const counts = (records || []).reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Disputed Records</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Facts a lead pushed back on, held against the value rather than the person, so one
            denial is visible on every thread that carries it.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/*
        This banner is load-bearing, not decoration. Until generateLeadDetailsBlock reads
        the dispute store, resolving a record changes NOTHING that a lead receives, and an
        admin who assumes otherwise would draw a false conclusion from a quiet inbox.
        DELETE THIS BANNER in the same commit that wires injection, not before or after.
      */}
      <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-900">
        <Info className="w-4 h-4 mt-0.5 flex-none" />
        <div>
          <span className="font-semibold">Shadow only.</span> Disputes are recorded and resolved
          here, but nothing on this page changes what a lead receives yet. Prompt injection has
          not been wired to this store. Resolving records now is what makes that switch safe to
          flip later.
        </div>
      </div>

      <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
        {FILTERS.map((f) => {
          const n = f.key === 'all' ? (records || []).length : (counts[f.key] || 0);
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-sm font-medium ${
                filter === f.key ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label} ({n})
            </button>
          );
        })}
      </div>

      {err && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
          Could not load disputed records: {err}
        </div>
      )}

      {loading && !records && (
        <div className="text-sm text-gray-500">Loading...</div>
      )}

      {records && visible.length === 0 && !err && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
          {filter === 'disputed'
            ? 'Nothing waiting on a decision.'
            : 'No records with this status.'}
        </div>
      )}

      <div className="space-y-2.5">
        {visible.map((r) => (
          <DisputeRow key={r.id} record={r} onResolved={load} />
        ))}
      </div>
    </div>
  );
}
